// Settings modal — first-run token entry and later edits. The WebSocket
// endpoint is same-origin (shown read-only); only the token is editable.

import { useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useSettingsStore } from '../../store/settingsStore';
import { CloseIcon } from '../ui/Icons';

interface Props {
  open: boolean;
  /** First-run flow — cannot be dismissed until a token is saved. */
  forced?: boolean;
  onClose: () => void;
}

export function SettingsSheet({ open, forced, onClose }: Props) {
  const token = useSettingsStore((s) => s.token);
  const deviceId = useSettingsStore((s) => s.deviceId);
  const wssUrl = useSettingsStore((s) => s.wssUrl);
  const setToken = useSettingsStore((s) => s.setToken);
  const clearToken = useSettingsStore((s) => s.clearToken);
  const connect = useChatStore((s) => s.connect);
  const disconnect = useChatStore((s) => s.disconnect);

  const [draft, setDraft] = useState(token);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function save() {
    if (!draft.trim()) {
      setError('Token required');
      return;
    }
    setError(null);
    setToken(draft.trim());
    disconnect();
    setTimeout(() => connect(), 100);
    onClose();
  }

  function clear() {
    if (!confirm('Clear the token and disconnect?')) return;
    disconnect();
    clearToken();
    setDraft('');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-divider bg-surface text-text-primary shadow-2xl">
        <div className="flex items-center justify-between border-b border-divider px-4 py-3">
          <h2 className="text-base font-semibold">Settings</h2>
          {!forced ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-text-secondary hover:text-text-primary"
            >
              <CloseIcon />
            </button>
          ) : null}
        </div>

        <div className="space-y-4 p-4">
          <Field label="Token" hint="Stored only in this browser's localStorage.">
            <input
              type="password"
              autoComplete="off"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              placeholder="API token"
              className="w-full rounded-lg border border-divider bg-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </Field>
          <Field label="Endpoint" hint="Served by the host — same origin as this page.">
            <input
              readOnly
              value={wssUrl}
              className="w-full rounded-lg border border-divider bg-elevated px-3 py-2 font-mono text-xs text-text-secondary"
            />
          </Field>
          <Field label="Device ID" hint="Stable per browser; identifies this device to the server.">
            <input
              readOnly
              value={deviceId}
              className="w-full rounded-lg border border-divider bg-elevated px-3 py-2 font-mono text-xs text-text-secondary"
            />
          </Field>
          <Field label="Build" hint="Identifies the frontend bundle this browser loaded.">
            <input
              readOnly
              value={__BUILD_ID__}
              className="w-full rounded-lg border border-divider bg-elevated px-3 py-2 font-mono text-xs text-text-secondary"
            />
          </Field>
          {error ? (
            <div className="rounded-md bg-error-bg px-2 py-1 text-xs text-error-text">{error}</div>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-divider px-4 py-3">
          {!forced ? (
            <button
              type="button"
              onClick={clear}
              className="rounded-lg bg-error-bg px-3 py-1.5 text-sm font-medium text-error-text hover:opacity-90"
            >
              Clear
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {!forced ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-3.5 py-1.5 text-sm font-medium text-text-secondary hover:bg-divider/40"
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              onClick={save}
              className="rounded-lg bg-accent px-3.5 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Save &amp; connect
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-text-faint">{hint}</div> : null}
    </label>
  );
}
